import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface AlibabaWanx25T2vPreviewParams {
  /**
   * 输入参数
   */
  input?: {
    /**
     * 文本提示词，描述要生成的视频内容
     * @length 0 - 2000
     */
    prompt: string;
    /**
     * 负面提示词，描述不希望出现在视频中的内容
     * @length 0 - 500
     */
    negative_prompt?: string;
    /**
     * 背景音乐URL
     */
    audio_url?: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 视频分辨率，支持 '832*480', '480*832', '624*624', '1280*720', '720*1280', '960*960', '1088*832', '832*1088', '1920*1080', '1080*1920', '1440*1440', '1632*1248', '1248*1632'
     * @default "1920*1080"
     * @values 832*480 | 480*832 | 624*624 | 1280*720 | 720*1280 | 960*960 | 1088*832 | 832*1088 | 1920*1080 | 1080*1920 | 1440*1440 | 1632*1248 | 1248*1632
     */
    size?: '832*480' | '480*832' | '624*624' | '1280*720' | '720*1280' | '960*960' | '1088*832' | '832*1088' | '1920*1080' | '1080*1920' | '1440*1440' | '1632*1248' | '1248*1632';
    /**
     * 视频时长，单位秒，支持5或10秒
     * @default 5
     * @values 5 | 10
     */
    duration?: 5 | 10;
    /**
     * 是否开启提示词增强
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
    /**
     * 是否生成音频
     * @default true
     */
    audio?: boolean;
    /**
     * 随机种子，用于可重现的生成结果
     * @range 0 - 2147483647
     */
    seed?: number;
  };
}

export interface AlibabaWanx25T2vPreviewResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx25_t2v_preview
 *
 * <p>Alibaba Wanx2.5 T2V Preview 视频生成实现。</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx25T2vPreview } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx25T2vPreview({
 *   // 参数见 AlibabaWanx25T2vPreviewParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx25T2vPreviewParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx25T2vPreview(
  params: AlibabaWanx25T2vPreviewParams,
): Promise<AlibabaWanx25T2vPreviewResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx25_t2v_preview',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx25T2vPreviewResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'video');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
